import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Empty,
  Grid,
  Icons,
  Row,
  Skeleton,
  Space,
  Typography,
} from '@pankod/refine-antd'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import React from 'react'
import Head from 'next/head'

import { BASE_PATH } from 'src/constant'
import { parseZendeskArticle } from './parser'
import {
  TParsedGuidanceArticle,
  TZendeskGuidanceError,
  TZendeskGuidanceResponse,
} from './types'

const { Paragraph, Text, Title } = Typography
const { LinkOutlined, RightOutlined } = Icons
const updatedAtFormatter = new Intl.DateTimeFormat('id-ID', {
  dateStyle: 'long',
  timeZone: 'Asia/Jakarta',
})

const renderCollapseIcon = ({ isActive }: { isActive?: boolean }) => (
  <RightOutlined
    aria-hidden
    className="transition-transform duration-200"
    style={{
      transform: `translateY(-50%) rotate(${isActive ? 90 : 0}deg)`,
    }}
  />
)

type TRequestState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'success'; data: TZendeskGuidanceResponse }

type TArticleNavigatorProps = {
  articles: TParsedGuidanceArticle[]
  expandedArticleId: string | null
  selectedArticleId?: string
  onExpandedArticleChange: (articleId: string | null) => void
  onAccordionClick: (articleId: string, accordionId: string) => void
}

const formatUpdatedAt = (updatedAt: string) => {
  const date = new Date(updatedAt)
  if (Number.isNaN(date.getTime())) return undefined

  return updatedAtFormatter.format(date)
}

const getErrorMessage = async (response: Response) => {
  try {
    const error = (await response.json()) as TZendeskGuidanceError
    return error.error.message
  } catch {
    return 'Panduan LMS Admin belum dapat dimuat.'
  }
}

const ArticleNavigator = ({
  articles,
  expandedArticleId,
  selectedArticleId,
  onExpandedArticleChange,
  onAccordionClick,
}: TArticleNavigatorProps) => {
  const handleChange = (activeKey: string | string[]) => {
    const nextKey = Array.isArray(activeKey) ? activeKey[0] : activeKey
    onExpandedArticleChange(nextKey ? String(nextKey) : null)
  }

  return (
    <nav aria-label="Daftar panduan LMS Admin">
      <Text
        type="secondary"
        className="mb-3 block text-xs font-semibold uppercase tracking-wide"
      >
        Daftar Panduan
      </Text>
      <Collapse
        accordion
        activeKey={expandedArticleId ?? undefined}
        className="panduan-lms-admin__navigator-collapse"
        expandIcon={renderCollapseIcon}
        expandIconPosition="end"
        onChange={handleChange}
      >
        {articles.map((article) => (
          <Collapse.Panel
            key={article.id}
            header={article.title}
            className={clsx(
              selectedArticleId === article.id &&
                'panduan-lms-admin__navigator-panel--selected',
            )}
          >
            {article.accordions.length > 0 ? (
              <Space direction="vertical" size={0} className="w-full">
                {article.accordions.map((accordion) => (
                  <Button
                    block
                    key={accordion.id}
                    type="text"
                    className="panduan-lms-admin__navigator-link"
                    onClick={() => onAccordionClick(article.id, accordion.id)}
                  >
                    {accordion.title}
                  </Button>
                ))}
              </Space>
            ) : (
              <Text type="secondary">
                Artikel ini tidak memiliki <i>section</i>.
              </Text>
            )}
          </Collapse.Panel>
        ))}
      </Collapse>
    </nav>
  )
}

type TArticleContentProps = {
  article: TParsedGuidanceArticle
  openAccordionIds: string[]
  onOpenAccordionIdsChange: (accordionIds: string[]) => void
}

const ArticleContent = React.memo(function ArticleContent({
  article,
  openAccordionIds,
  onOpenAccordionIdsChange,
}: TArticleContentProps) {
  const updatedAt = formatUpdatedAt(article.updatedAt)

  const handleAccordionChange = (activeKey: string | string[]) => {
    onOpenAccordionIdsChange(
      (Array.isArray(activeKey) ? activeKey : [activeKey]).map(String),
    )
  }

  return (
    <article aria-labelledby="selected-article-title">
      <div>
        <Title
          id="selected-article-title"
          level={2}
          tabIndex={-1}
          className="panduan-lms-admin__focus-target !mb-2 !mt-3"
        >
          {article.title}
        </Title>
        <Space wrap size="middle">
          {updatedAt ? (
            <Text type="secondary">Diperbarui {updatedAt}</Text>
          ) : null}
          <Typography.Link
            href={article.htmlUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Lihat artikel asli
            <LinkOutlined aria-hidden className="ml-1" />
          </Typography.Link>
        </Space>
      </div>
      <Divider />

      {article.introHtml ? (
        <div
          className="panduan-lms-admin__article-body"
          dangerouslySetInnerHTML={{ __html: article.introHtml }}
        />
      ) : null}

      <Collapse
        activeKey={openAccordionIds}
        className="!mt-6"
        expandIcon={renderCollapseIcon}
        expandIconPosition="end"
        onChange={handleAccordionChange}
      >
        {article.accordions.map((accordion) => {
          return (
            <Collapse.Panel
              key={accordion.id}
              header={
                <h3
                  id={accordion.id}
                  tabIndex={-1}
                  className="panduan-lms-admin__focus-target !m-0 scroll-mt-24 text-base font-semibold"
                >
                  {accordion.title}
                </h3>
              }
            >
              <div
                className="panduan-lms-admin__article-body"
                dangerouslySetInnerHTML={{ __html: accordion.panelHtml }}
              />
            </Collapse.Panel>
          )
        })}
      </Collapse>
    </article>
  )
})

export const PanduanLmsAdmin: React.FC = () => {
  const router = useRouter()
  const breakpoint = Grid.useBreakpoint()
  const isDesktop = Boolean(breakpoint.lg)
  const abortControllerRef = React.useRef<AbortController>()
  const lastScrolledAccordionLocationRef = React.useRef<string | null>(null)
  const pendingArticleNavigationFrameRef = React.useRef<number | null>(null)
  const shouldFocusSelectedArticleRef = React.useRef(false)
  const [requestState, setRequestState] = React.useState<TRequestState>({
    status: 'loading',
  })
  const [expandedArticleId, setExpandedArticleId] = React.useState<
    string | null
  >(null)
  const [openAccordionIds, setOpenAccordionIds] = React.useState<string[]>([])
  const [isMobileNavigatorExpanded, setIsMobileNavigatorExpanded] =
    React.useState(true)

  const cancelPendingArticleNavigation = React.useCallback(() => {
    const pendingFrame = pendingArticleNavigationFrameRef.current
    if (pendingFrame !== null && pendingFrame >= 0) {
      cancelAnimationFrame(pendingFrame)
    }
    pendingArticleNavigationFrameRef.current = null
  }, [])

  const loadArticles = React.useCallback(async () => {
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller
    setRequestState({ status: 'loading' })

    try {
      const response = await fetch(`${BASE_PATH || ''}/api/panduan-lms-admin`, {
        signal: controller.signal,
      })
      if (!response.ok) {
        throw new Error(await getErrorMessage(response))
      }

      const data = (await response.json()) as TZendeskGuidanceResponse
      setRequestState({ status: 'success', data })
    } catch (error: unknown) {
      if (controller.signal.aborted) return
      setRequestState({
        status: 'error',
        message:
          error instanceof Error
            ? error.message
            : 'Panduan LMS Admin belum dapat dimuat.',
      })
    }
  }, [])

  React.useEffect(() => {
    loadArticles()
    return () => {
      abortControllerRef.current?.abort()
      cancelPendingArticleNavigation()
    }
  }, [cancelPendingArticleNavigation, loadArticles])

  const parsedArticles = React.useMemo(
    () =>
      requestState.status === 'success'
        ? requestState.data.articles.map(parseZendeskArticle)
        : [],
    [requestState],
  )
  const articleById = React.useMemo(
    () => new Map(parsedArticles.map((article) => [article.id, article])),
    [parsedArticles],
  )
  const queryArticleId =
    typeof router.query.articleId === 'string'
      ? router.query.articleId
      : undefined
  const selectedArticle = queryArticleId
    ? articleById.get(queryArticleId)
    : undefined

  React.useEffect(() => {
    if (selectedArticle) {
      setExpandedArticleId(selectedArticle.id)
      setIsMobileNavigatorExpanded(false)
    }
  }, [selectedArticle])

  React.useEffect(() => {
    setOpenAccordionIds([])
  }, [selectedArticle?.id])

  const focusSelectedArticle = React.useCallback(() => {
    requestAnimationFrame(() => {
      const target = document.getElementById('selected-article-title')
      target?.scrollIntoView({ behavior: 'instant', block: 'start' })
      target?.focus({ preventScroll: true })
    })
  }, [])

  React.useEffect(() => {
    if (!selectedArticle || !shouldFocusSelectedArticleRef.current) return

    shouldFocusSelectedArticleRef.current = false
    focusSelectedArticle()
  }, [focusSelectedArticle, selectedArticle])

  const scrollToAccordion = React.useCallback(
    (articleId: string, accordionId: string, shouldOpenAccordion = true) => {
      lastScrolledAccordionLocationRef.current = `${articleId}#${accordionId}`
      if (shouldOpenAccordion) {
        setOpenAccordionIds((current) =>
          current.includes(accordionId) ? current : [...current, accordionId],
        )
      }
      requestAnimationFrame(() => {
        const target = document.getElementById(accordionId)
        target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        target?.focus({ preventScroll: true })
      })
    },
    [],
  )

  React.useEffect(() => {
    if (!router.isReady || !selectedArticle) return
    if (!window.location.hash) {
      lastScrolledAccordionLocationRef.current = null
      return
    }

    let accordionId = window.location.hash.slice(1)
    try {
      accordionId = decodeURIComponent(accordionId)
    } catch {
      return
    }

    if (
      selectedArticle.accordions.some(
        (accordion) => accordion.id === accordionId,
      )
    ) {
      const locationKey = `${selectedArticle.id}#${accordionId}`
      if (lastScrolledAccordionLocationRef.current === locationKey) return
      scrollToAccordion(selectedArticle.id, accordionId)
    }
  }, [router.asPath, router.isReady, scrollToAccordion, selectedArticle])

  const scheduleArticleNavigation = React.useCallback(
    (articleId: string) => {
      cancelPendingArticleNavigation()
      pendingArticleNavigationFrameRef.current = -1
      const frameId = requestAnimationFrame(() => {
        pendingArticleNavigationFrameRef.current = null
        void router.replace(
          {
            pathname: router.pathname,
            query: { ...router.query, articleId },
          },
          undefined,
          { shallow: true, scroll: false },
        )
      })
      if (pendingArticleNavigationFrameRef.current !== null) {
        pendingArticleNavigationFrameRef.current = frameId
      }
    },
    [cancelPendingArticleNavigation, router],
  )

  const handleExpandedArticleChange = React.useCallback(
    (articleId: string | null) => {
      setExpandedArticleId(articleId)
      if (!articleId) {
        cancelPendingArticleNavigation()
        return
      }

      const isChangingArticle = queryArticleId !== articleId
      if (isChangingArticle) {
        shouldFocusSelectedArticleRef.current = true
      }

      if (!isDesktop) {
        setIsMobileNavigatorExpanded(false)
        if (!isChangingArticle) {
          focusSelectedArticle()
        }
      }

      if (!isChangingArticle) {
        cancelPendingArticleNavigation()
        return
      }

      scheduleArticleNavigation(articleId)
    },
    [
      cancelPendingArticleNavigation,
      focusSelectedArticle,
      isDesktop,
      queryArticleId,
      scheduleArticleNavigation,
    ],
  )

  const handleAccordionClick = React.useCallback(
    (articleId: string, accordionId: string) => {
      setExpandedArticleId(articleId)
      setIsMobileNavigatorExpanded(false)
      void router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, articleId },
          hash: accordionId,
        },
        undefined,
        { shallow: true, scroll: false },
      )
      scrollToAccordion(articleId, accordionId, false)
    },
    [router, scrollToAccordion],
  )

  const navigator =
    requestState.status === 'success' ? (
      <ArticleNavigator
        articles={parsedArticles}
        expandedArticleId={expandedArticleId}
        selectedArticleId={selectedArticle?.id}
        onExpandedArticleChange={handleExpandedArticleChange}
        onAccordionClick={handleAccordionClick}
      />
    ) : requestState.status === 'loading' ? (
      <Skeleton active paragraph={{ rows: 6 }} title={false} />
    ) : null

  let content: React.ReactNode
  if (requestState.status === 'loading') {
    content = <Skeleton active paragraph={{ rows: 8 }} />
  } else if (requestState.status === 'error') {
    content = (
      <div>
        <Alert
          showIcon
          type="error"
          message="Panduan tidak dapat dimuat"
          description={requestState.message}
        />
        <Button className="mt-4" type="primary" onClick={loadArticles}>
          Coba lagi
        </Button>
      </div>
    )
  } else if (parsedArticles.length === 0) {
    content = <Empty description="Belum ada panduan pada bagian Zendesk ini." />
  } else if (queryArticleId && !selectedArticle) {
    content = (
      <Alert
        showIcon
        type="warning"
        message="Panduan tidak ditemukan"
        description="Pilih panduan lain dari daftar di sebelah kiri."
      />
    )
  } else if (selectedArticle) {
    content = (
      <ArticleContent
        article={selectedArticle}
        openAccordionIds={openAccordionIds}
        onOpenAccordionIdsChange={setOpenAccordionIds}
      />
    )
  } else {
    content = (
      <div className="flex h-full items-center justify-center">
        <Empty description="Pilih panduan dari daftar untuk melihat isi artikel." />
      </div>
    )
  }

  return (
    <>
      <Head>
        <title>LMS Admin | Panduan LMS Admin</title>
      </Head>
      <div
        className={clsx(
          'panduan-lms-admin',
          isDesktop && 'panduan-lms-admin--desktop',
        )}
      >
        <div className="panduan-lms-admin__page-heading mb-6">
          <Title level={3} className="!mb-1">
            Panduan LMS Admin
          </Title>
          <Paragraph type="secondary" className="!mb-0">
            Daftar panduan ini berisi artikel yang dapat membantu Anda dalam
            mengelola LMS Admin.
          </Paragraph>
        </div>

        <Row
          align="top"
          className={clsx(isDesktop && 'panduan-lms-admin__desktop-layout')}
          gutter={isDesktop ? [24, 0] : [24, 24]}
          wrap={!isDesktop}
        >
          {isDesktop ? (
            <Col className="panduan-lms-admin__desktop-column" flex="320px">
              <aside className="panduan-lms-admin__navigator-pane">
                <Card
                  className="panduan-lms-admin__panel-card panduan-lms-admin__navigator-card"
                  size="small"
                >
                  {navigator}
                </Card>
              </aside>
            </Col>
          ) : (
            <Col span={24}>
              <Collapse
                activeKey={isMobileNavigatorExpanded ? ['navigator'] : []}
                expandIcon={renderCollapseIcon}
                expandIconPosition="end"
                onChange={(activeKey) =>
                  setIsMobileNavigatorExpanded(
                    (Array.isArray(activeKey) ? activeKey : [activeKey])
                      .length > 0,
                  )
                }
              >
                <Collapse.Panel key="navigator" header="Daftar Panduan">
                  {navigator}
                </Collapse.Panel>
              </Collapse>
            </Col>
          )}

          <Col
            className={clsx(isDesktop && 'panduan-lms-admin__desktop-column')}
            flex={isDesktop ? '1 1 0' : undefined}
            span={isDesktop ? undefined : 24}
          >
            <main
              className={clsx(
                'min-w-0',
                isDesktop && 'panduan-lms-admin__article-pane',
              )}
            >
              <Card
                className={clsx(
                  isDesktop &&
                    'panduan-lms-admin__panel-card panduan-lms-admin__article-card',
                )}
              >
                <div
                  className="panduan-lms-admin__article-scroll-region"
                  data-testid="panduan-article-scroll-region"
                >
                  {content}
                </div>
              </Card>
            </main>
          </Col>
        </Row>
      </div>
    </>
  )
}
